<template>
  <div>
    <div class="product-agenda-bar__wrapper">
      <div class="product-agenda-bar">
        <div class="product-agenda-bar__content">
          <div
            class="product-agenda-bar__title d-flex justify-space-between align-center"
          >
            <h1>
              {{ productDocument.title }}
            </h1>

            <div v-if="showPrices" class="prices d-flex align-center">
              <price
                :price="price"
                class="text-color-tertiary text-color-md-secondary"
                :text="$t('purchase.price.preco')"
              />

              <price
                v-if="showCompareAtPrice"
                :compare="true"
                :price="compareAtPrice"
                :text="$t('purchase.price.stock')"
              />
            </div>
          </div>

          <div ref="purchaseColors" class="product-agenda-bar__colors">
            <color-picker
              v-if="colorsDimension.length > 1"
              v-model="selectedDimensions.color"
              :colors="colorsDimension"
              :product-document="productDocument"
              @input="onColorChange"
            />
          </div>
        </div>

        <div class="product-agenda-bar__actions">
          <primary-button
            id="Subscribe"
            :text="$t('create_alert.alert')"
            align="center"
            type="button"
            aspect="yellow"
            no-shadow
            class="add-to-cart"
            @click="onShowCreateAlert"
          />

          <transition
            @before-enter="onBeforeEnter"
            @enter="onEnter"
            @leave="onLeave"
          >
            <create-alert
              v-if="showCreateAlert"
              id="purchase"
              ref="createAlert"
              :product="bisProduct"
              :title="createAlertTitle"
              :text="createAlertText"
              :date="productInSaleDate"
              :origin="ORIGIN.PRODUCT"
              button-aspect="yellow"
            />
          </transition>
        </div>

        <div
          v-if="productInSaleDate"
          class="product-agenda-bar__in-sale-date mb-0-4"
        >
          <icon name="estimated-time" />
          <p class="mb-0">
            {{
              $t('lauch_day', {
                day: $d(productInSaleDate, 'weekday'),
              })
            }}
          </p>
        </div>
      </div>

      <div v-if="soldOut" class="product-agenda-bar__sold-out mt-1 mb-1">
        <p class="mb-0 text-small text-md-base text-color-tertiary">
          {{
            $t(
              [10286453621069, 10286453784909].includes(
                parseInt(productDocument.shopify_product.id, 10)
              )
                ? 'product.full_soldout_polaire'
                : 'product.soldout_v2_stock'
            )
          }}
        </p>
      </div>

      <div class="product-agenda-bar__links">
        <button class="btn-link" @click="handleShowModal('size')">
          <div class="text-secondary lh-1 pt-0-2">
            <span class="text-small">
              {{ $t('purchase.size-guide') }}
            </span>
          </div>
        </button>

        <nuxt-link
          v-if="hasCareGuide"
          class="btn-link"
          target="_blank"
          :to="
            $contextPath(
              `/blogs/guides-dentretien/${productDocument.care_guide.uid}`
            )
          "
        >
          <div class="text-secondary lh-1 pt-0-2">
            <span class="text-small">
              {{ $t('order.careGuide') }}
            </span>
          </div>
        </nuxt-link>
      </div>
    </div>
    <purchase-modal
      :title="modal.show ? $t(`purchase.modal.${modal.type}`) : ''"
      :show="modal.show"
      full-page
      desktop-override-purchase
      :class="`purchase-modal--page-${modal.type}`"
      @hide="handleHideModal"
    >
      <template #content>
        <transition mode="out-in" name="fade">
          <div v-if="modal.loading" class="mt-1">
            <p class="d-flex justify-center align-center">
              <loading-icon class="mr-0-6" />
              <span>{{ $t('loading') }}</span>
            </p>
          </div>

          <div v-else-if="modal.content">
            <blocks
              v-if="modal.content.blocks"
              :blocks="modal.content.blocks"
              :entities="{
                activeSize: null,
              }"
            />
          </div>
        </transition>
      </template>
    </purchase-modal>
  </div>
</template>

<script>
import slugify from 'slugify'

import { ORIGIN, STATUS } from '@/const'
import { EVENTS } from '@/const/events'
import {
  isProductWillSoonBeForSale,
  PRODUCT_COLOR_STATUS,
} from '@/entities/sanity/product'
import ShopifyProduct from '@/entities/shopify/Product'

import { getSanityBunnyImageLink } from '@/utils/image'
import services from '@/services'

import Price from '@/components/Price/Price'
import ColorPicker from '@/components/picker/ColorPicker'
import PrimaryButton from '@/components/buttons/Primary'
import Icon from '@/components/icons/Icon'
import CreateAlert from '@/components/Product/CreateAlert'
import PurchaseModal from '@/components/modal/PurchaseModal'
import LoadingIcon from '@/components/icons/Loading'
import Blocks from '@/components/blocks/Blocks'

export default {
  name: 'ProductAgendaBar',
  components: {
    Price,
    ColorPicker,
    PrimaryButton,
    Icon,
    CreateAlert,
    PurchaseModal,
    LoadingIcon,
    Blocks,
  },
  props: {
    productDocument: {
      type: Object,
      required: true,
    },
    shopifyProduct: {
      type: Object,
      required: true,
    },
    soldOut: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedDimensions: {
        color: null,
        third: null,
      },
      showCreateAlert: false,
      ORIGIN,

      modal: {
        show: false,
        type: '',
        loading: false,
        content: null,
      },
    }
  },
  computed: {
    /**
     * Couleur
     */
    colorsDimension() {
      const cc = []

      for (const color of this.productDocument.colors) {
        if (
          this.shopifyProduct.isDimensionHasValue(
            'color',
            this.getColorFromColorKey(color._key)
          )
        ) {
          let shouldAddColor = true
          if (this.canSelectThirdDimension && this.selectedDimensions.third) {
            const colorsOfSelectedThirdDimension =
              this.shopifyProduct.getDimensionsByDimension(
                this.thirdDimensionName,
                this.selectedDimensions.third,
                'color'
              )
            shouldAddColor = colorsOfSelectedThirdDimension.includes(
              this.getColorFromColorKey(color._key)
            )
          }

          if (!cc.some((c) => c.value === color._key) && shouldAddColor) {
            let label = color.label

            if (this.hasThirdDimension) {
              label =
                this.productDocument.shopify_product.colorOptions.find(
                  (colorOption) => color._key.endsWith(colorOption._key)
                )?.name || label
            } else {
              label =
                this.productDocument.shopify_product.colorOptions.find(
                  (colorOption) => colorOption._key === color._key
                )?.name || label
            }

            const c = {
              value: this.getColorFromColorKey(color._key),
              label,
              isNew: color.color_status === PRODUCT_COLOR_STATUS.NEW,
              isLeavingSoon:
                color.color_status === PRODUCT_COLOR_STATUS.LEAVING_SOON,
              image: {
                url: getSanityBunnyImageLink(color.thumbnail, {
                  width: 100,
                  height: 100,
                }),
                dimensions: {
                  width: 100,
                  height: 100,
                },
              },
            }

            if (this.canSelectThirdDimension) {
              const mixedDimension = {
                color: c.value,
              }
              mixedDimension[this.thirdDimensionName] =
                this.selectedDimensions.third

              c.available =
                this.shopifyProduct.isMixedDimensionsAvailable(mixedDimension)

              c[this.thirdDimensionName] =
                this.shopifyProduct.getDimensionsByDimension(
                  'color',
                  c.value,
                  this.thirdDimensionName
                )[0]
            } else {
              c.available = this.shopifyProduct.isDimensionAvailable(
                'color',
                c.value
              )

              if (this.hasThirdDimension) {
                c[this.thirdDimensionName] =
                  this.shopifyProduct.getDimensionsByDimension(
                    'color',
                    c.value,
                    this.thirdDimensionName
                  )[0]
              }
            }

            if (this.productIsOnSale && !c.available) {
              continue
            }

            cc.push(c)
          }
        }
      }

      return cc
    },

    /**
     * Troisieme dimension
     */
    hasThirdDimension() {
      return (
        this.productDocument.shopify_product.options.length > 2 &&
        this.productDocument.shopify_product.options.some(
          (o) => o.position === 3
        )
      )
    },
    thirdDimensionName() {
      if (this.hasThirdDimension) {
        return slugify(
          this.productDocument.shopify_product.options.find(
            (o) => o.position === 3
          ).name,
          { lower: true, strict: true }
        )
      }

      return ''
    },
    thirdDimensions() {
      const dd = []

      if (this.hasThirdDimension) {
        for (const d of this.productDocument.third_options.list) {
          if (
            this.shopifyProduct.isDimensionHasValue(
              this.thirdDimensionName,
              slugify(d.name, { lower: true, strict: true })
            )
          ) {
            dd.push({
              value: slugify(d.name, { lower: true, strict: true }),
              label: d.name,
              desc: d.desc,
              image: d.image,
              available: this.shopifyProduct.isDimensionAvailable(
                this.thirdDimensionName,
                slugify(d.name, { lower: true, strict: true })
              ),
            })
          }
        }
      }

      return dd
    },
    canSelectThirdDimension() {
      return (
        this.hasThirdDimension &&
        this.productDocument.third_options.layout === 'as_selector'
      )
    },
    selectedThirdDimension() {
      if (this.hasThirdDimension) {
        return (
          this.thirdDimensions.find(
            (d) => d.value === this.selectedDimensions.third
          ) || null
        )
      }

      return null
    },

    hasCareGuide() {
      return (
        this.productDocument.care_guide && this.productDocument.care_guide.uid
      )
    },

    /**
     * Prix du produit
     */
    showPrices() {
      return this.productDocument && this.shopifyProduct
    },
    price() {
      if (!this.shopifyProduct) {
        return null
      }

      if (this.selectedDimensions.color) {
        return this.shopifyProduct.priceByDimension(
          'color',
          this.selectedDimensions.color
        )
      }

      return this.shopifyProduct.price
    },
    compareAtPrice() {
      if (!this.shopifyProduct) {
        return null
      }

      if (this.selectedDimensions.color) {
        return this.shopifyProduct.compareAtPriceByDimension(
          'color',
          this.selectedDimensions.color
        )
      }

      return this.shopifyProduct.compareAtPrice
    },
    showCompareAtPrice() {
      if (this.showPrices) {
        return (
          this.productStatus !== STATUS.STOCK &&
          this.price !== this.compareAtPrice
        )
      }

      return false
    },

    /**
     * Lancement du produit
     */
    productInSaleDate() {
      const now = new Date()

      if (this.shopifyProduct.startDate) {
        return this.shopifyProduct.startDate > now
          ? this.shopifyProduct.startDate
          : false
      }

      if (isProductWillSoonBeForSale(this.productDocument)) {
        return this.productDocument.dates.start
      }

      return false
    },

    /** CREATE ALERT */
    bisProduct() {
      return {
        productSlug: this.productDocument.uid,
        productName: this.productDocument.title,
        productId: this.shopifyProduct.id,
      }
    },
    createAlertTitle() {
      return this.$t('product.back-one-day')
    },
    createAlertText() {
      if (ShopifyProduct.willBeInStock(this.shopifyProduct)) {
        return this.$t('product.receive-alert-stock')
      }

      return this.$t('product.receive-alert-preorder')
    },
  },
  created() {
    this.updateSelectedColorAndThird()
  },

  beforeDestroy() {
    window.removeEventListener('click', this.onBodyClose)
  },

  methods: {
    /**
     * Permet d'extraire la couleur de la clé couleur d'un productDocument.colors[]_key
     * @param colorKey La clé de la couleur (avec ou sans la 3ème dimension) selon le format "third__color"
     * @returns La couleur correspondante
     */
    getColorFromColorKey(colorKey) {
      return this.hasThirdDimension ? colorKey.split('__')[1] : colorKey
    },
    /**
     * Permet d'extraire la troisième dimension de la clé couleur d'un productDocument.colors[]_key
     * @param colorKey La clé de la couleur (avec ou sans la 3ème dimension) selon le format "third__color"
     * @returns La third correspondante
     */
    getThirdFromColorKey(colorKey) {
      return this.hasThirdDimension ? colorKey.split('__')[0] : colorKey
    },
    getColorKeyFromDimensions(dimensions) {
      if (this.hasThirdDimension) {
        return `${dimensions.third}__${dimensions.color}`
      }
      return dimensions.color
    },

    updateSelectedColorAndThird() {
      if (
        this.productDocument.colors &&
        this.productDocument.colors.length > 0
      ) {
        if (this.$route.query.color) {
          if (this.hasThirdDimension && this.canSelectThirdDimension) {
            if (this.$route.query[this.thirdDimensionName]) {
              // IF the third dimension is selectable
              // THEN we have to search the color with its color name and the third dimension
              // AS the color name could be the same for different third dimensions
              const c = this.productDocument.colors.find(
                (c) =>
                  `${this.$route.query[this.thirdDimensionName]}__${
                    this.$route.query.color
                  }` === c._key
              )

              if (c) {
                this.selectedDimensions.color = this.getColorFromColorKey(
                  c._key
                )
                this.selectedDimensions.third = this.getThirdFromColorKey(
                  c._key
                )

                return
              }
            }
          } else {
            // IF no the third dimension or it is not selectable,
            // THEN we can search the color directly with its color name
            // AS the color name is considered as unique
            const c = this.productDocument.colors.find(
              (color) =>
                this.getColorFromColorKey(color._key) ===
                this.$route.query.color
            )

            if (c) {
              this.selectedDimensions.color = this.getColorFromColorKey(c._key)

              if (this.hasThirdDimension && this.thirdDimensionName) {
                this.selectedDimensions.third = this.getThirdFromColorKey(
                  c._key
                )
              }

              return
            }
          }
        }

        // IF no color is selected from the URL,
        // THEN we select the first color from the product document
        // AND the third dimension if it exists
        this.selectedDimensions.color = this.productDocument.colors[0]
          ? this.getColorFromColorKey(this.productDocument.colors[0]._key)
          : null

        if (this.hasThirdDimension) {
          this.selectedDimensions.third = this.productDocument.colors[0]
            ? this.getThirdFromColorKey(this.productDocument.colors[0]._key)
            : null
        }
      }
    },

    onColorChange(_, color) {
      if (
        this.hasThirdDimension &&
        !this.canSelectThirdDimension &&
        color[this.thirdDimensionName]
      ) {
        this.selectedDimensions.third = color[this.thirdDimensionName]
      }

      this.updateState(true)
    },

    updateState(fromUserAction = false) {
      this.$nuxt.$emit(
        EVENTS.PRODUCT_COLOR_UPDATED,
        this.getColorKeyFromDimensions(this.selectedDimensions)
      )

      this.updateSelectedColorScroll()

      if (fromUserAction) {
        this.updateUrlParams()
      }
    },

    updateSelectedColorScroll() {
      if (this.$refs.purchaseColors) {
        const selectedColor = this.$refs.purchaseColors.getElementsByClassName(
          'picker-item--active'
        )
        if (selectedColor && selectedColor[0]) {
          selectedColor[0].scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          })
        }
      }
    },

    /**
     * Gère les paramètres de la route
     */
    updateUrlParams() {
      if (process.client) {
        const url = new URL(window.location.href)

        if (this.selectedDimensions.color) {
          url.searchParams.set('color', this.selectedDimensions.color)
        } else {
          url.searchParams.delete('color')
        }

        if (this.hasThirdDimension && this.selectedDimensions.third) {
          url.searchParams.set(
            this.thirdDimensionName,
            this.selectedDimensions.third
          )
        } else {
          url.searchParams.delete(this.thirdDimensionName)
        }

        window.history.replaceState(null, null, url)
      }
    },

    onShowCreateAlert(event) {
      event.preventDefault()
      event.stopPropagation()
      this.showCreateAlert = true

      window.addEventListener('click', this.onBodyClose)
    },

    onHideCreateAlert() {
      this.showCreateAlert = false
      window.removeEventListener('click', this.onBodyClose)
    },

    onBodyClose(event) {
      const target = event.target
      if (this.$refs.createAlert) {
        const isClickInside = this.$refs.createAlert.$el.contains(target)

        if (!isClickInside) {
          this.onHideCreateAlert()
        }
      } else {
        this.onHideCreateAlert()
      }
    },

    handleShowModal(type) {
      this.modal.show = true

      if (this.modal.content === null || this.modal.type !== type) {
        this.modal.loading = true

        let id = null
        if (type === 'delivery') {
          id = this.productDocument.delivery_and_returns._ref
        } else if (type === 'size') {
          id = this.productDocument.size_guide._ref
        }

        services.sanityService
          .getById({
            id,
            lang: this.$i18n.locale.slice(0, 2),
          })

          .then((document) => {
            this.modal.content = document
            this.modal.type = type
          })
          .catch((err) => {
            this.$logError.captureException(err)
          })
          .finally(() => {
            this.modal.loading = false
          })
      }
    },

    handleHideModal() {
      if (this.modal.show || this.showTestimonialsModal) {
        this.modal.show = false
        this.showTestimonialsModal = false
      } else {
        this.showSizeSelector = false
      }
    },

    handleOverlayClick() {
      if (this.showSizeSelector) {
        this.handleShowSizeSelector()
      }
    },

    async onBeforeEnter(el) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      const rect = el.getBoundingClientRect()

      if (rect.right > window.innerWidth) {
        const diff = rect.right - window.innerWidth + 15

        gsap.set(el, {
          marginLeft: -diff,
        })
      }

      let init = { y: 10 }
      if (window.innerWidth > 769) {
        init = {
          x: -10,
        }
      }

      gsap.set(el, {
        ...init,
        opacity: 0,
        overflow: 'hidden',
      })
    },

    async onEnter(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      gsap.to(el, {
        opacity: 1,
        x: 0,
        y: 0,
        duration: 0.3,
        ease: 'power2.in',
        onComplete: () => {
          gsap.set(el, { overflow: 'visible' })

          if (window.innerWidth <= 769) {
            gsap.set(this.$el, {
              backdropFilter: 'none',
            })
          }

          done()
        },
      })
    },
    async onLeave(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      let init = { y: 10 }
      if (window.innerWidth > 769) {
        init = {
          x: -10,
        }
      }

      if (window.innerWidth <= 769) {
        this.$el.style = ''
      }

      gsap.to(el, {
        ...init,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: done,
      })
    },
  },
}
</script>

<style lang="scss">
.product-agenda-bar__wrapper {
  position: fixed;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;

  @include mq($until: tablet) {
    left: var(--spacing);
    right: var(--spacing);
    bottom: calc(env(safe-area-inset-bottom) + var(--spacing));
    padding-top: var(--spacing);
  }

  @include mq($from: tablet) {
    left: 50%;
    bottom: var(--spacing);
    transform: translateX(-50%);
  }

  &::after {
    content: '';
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    height: 250px;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.4) 100%
    );
    z-index: -1;

    @include mq($from: tablet) {
      display: none;
    }
  }
}

.product-agenda-bar {
  padding: calc(var(--spacing) * 0.6);
  background: RGB(0 0 0 / 0.4);
  backdrop-filter: blur(25px);

  @include mq($until: tablet) {
    padding-top: var(--spacing);
    width: 100%;
  }

  @include mq($from: tablet) {
    display: flex;
    align-items: stretch;
    gap: calc(var(--spacing) * 2);
  }

  h1,
  .price__price {
    color: white;
    margin-bottom: 0;
    font-size: var(--base-font-size);

    @include mq($from: tablet) {
      font-size: var(--big-font-size);
      white-space: nowrap;
    }
  }

  .prices {
    gap: calc(var(--spacing) * 0.6);
  }

  .price__text {
    @include visually-hidden;
  }

  .picker__elements {
    justify-content: flex-start;
  }

  .picker {
    margin-bottom: 0;

    @include mq($until: tablet) {
      padding: 0 calc(var(--spacing) * 0.6) var(--spacing);
    }

    &.picker--color {
      .picker__elements {
        flex-wrap: nowrap;
      }

      .picker__element {
        margin: 0 rem(5px);

        &.break {
          display: none;
        }
      }
    }
  }

  .picker-item {
    --line-color: transparent;
    --line-position: 0;

    min-width: rem(32px);
    min-height: rem(32px);

    &:not(.picker-item--active):hover {
      --line-color: #{rgba($tertiary-color, 0.7)};
      --line-position: #{rem(-1px)};
    }
  }

  .picker-item--active {
    --line-color: var(--tertiary-color);
    --line-position: #{rem(-2px)};
  }

  .picker-item__bg {
    &::after {
      box-shadow: 0 0 0 rem(1px) var(--line-color);
    }
  }

  .picker__label-container {
    @include mq($until: tablet) {
      justify-content: start;

      .picker__label {
        font-size: var(--extra-small-font-size);
        margin-bottom: calc(var(--spacing) * 0.6);
        color: white;
      }
    }

    @include mq($from: tablet) {
      @include visually-hidden;
    }
  }

  .action {
    min-width: rem(190px);

    @include mq($until: tablet) {
      width: 100%;
    }
  }

  .create-alert {
    position: absolute;
    background: RGB(0 0 0 / 0.4);
    backdrop-filter: blur(25px);
    z-index: 10;

    @include mq($until: tablet) {
      bottom: calc((env(safe-area-inset-bottom) + var(--spacing)) * -1);
      left: calc(var(--spacing) * -1);
      right: calc(var(--spacing) * -1);
      padding: calc(var(--spacing) * 1.6) calc(var(--spacing) * 1.6)
        calc(var(--spacing) * 1.6);
    }

    @include mq($from: tablet) {
      left: calc(var(--spacing) * -2);
      bottom: calc(var(--spacing) * -0.6);
      width: rem(394px);
      padding: calc(var(--spacing) * 1.6) calc(var(--spacing) * 1.6)
        calc(var(--spacing) * 0.6);
    }

    .create-alert__title {
      font-size: var(--base-font-size);
    }

    .create-alert__desc {
      color: var(--tertiary-color);
    }

    .action {
      width: 100%;
    }
  }
}

.product-agenda-bar__content {
  @include mq($from: tablet) {
    display: flex;
    gap: calc(var(--spacing) * 2);
    align-items: center;
  }
}

.product-agenda-bar__title {
  gap: calc(var(--spacing) * 2);

  @include mq($until: tablet) {
    padding: 0 calc(var(--spacing) * 0.6);
    margin-bottom: calc(var(--spacing) * 0.6);
  }

  @include mq($from: tablet) {
    padding-left: calc(var(--spacing) * 1.4);
  }
}

.product-agenda-bar__actions {
  @include mq($from: tablet) {
    position: relative;
  }
}

.product-agenda-bar__in-sale-date {
  position: absolute;
  bottom: 100%;
  background-color: var(--tertiary-color);
  color: var(--secondary-color);
  font-family: var(--tertiary-font-family);
  font-size: var(--small-font-size);
  line-height: 1;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: calc(var(--spacing) * 0.4);

  @include mq($until: tablet) {
    padding: calc(var(--spacing) * 0.4) var(--spacing)
      calc(var(--spacing) * 0.4) calc(var(--spacing) * 0.6);
    left: 0;
  }

  @include mq($from: tablet) {
    padding: var(--spacing) calc(var(--spacing) * 0.6);
    left: 50%;
    transform: translateX(-50%);
  }

  .icon {
    width: rem(14px);
    height: rem(14px);
  }
}

.product-agenda-bar__links {
  margin-top: rem(4px);
  display: flex;
  align-items: center;
  justify-content: space-between;

  @include mq($from: tablet) {
    filter: drop-shadow(0 0 7px rgba(0, 0, 0, 0.4));
    justify-content: center;
    gap: rem(38px);
  }

  .btn-link {
    text-decoration: underline;
    color: var(--tertiary-color);
  }
}

.product-agenda-bar__sold-out {
  padding: var(--spacing) calc(var(--spacing) * 2);
  background: RGB(0 0 0 / 0.4);
  backdrop-filter: blur(25px);
}
</style>
